import React, { useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Grid,
  GridItem,
  HStack,
  Input,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useToast,
  VisuallyHidden,
} from "@chakra-ui/react";
import { FiCheck, FiEdit2, FiPlus, FiTrash2, FiTruck } from "react-icons/fi";

import {
  Suppliers,
  SupplierInfo,
} from "../../../../types/requestCreditDataTypes";
import { DataTable, EmptyState } from "../../../ui";

interface SuppliersStepProps {
  savedData: Suppliers;
  updateData: (data: Suppliers) => void;
}

interface UpdatedSupplierInfo extends SupplierInfo {
  index: number;
}

const SuppliersStep: React.FC<SuppliersStepProps> = ({
  savedData = [],
  updateData,
}) => {
  const toast = useToast();
  const suppliers = Array.isArray(savedData) ? savedData : [];


  const [editEnabled, setEditEnabled] = useState<boolean>(false);

  const [selectedUser, setSelectedUser] = useState<UpdatedSupplierInfo>({
    name: "",
    contact: "",
    address: "",
    designation: "",
    email: "",
    phone: "",
    index: -1,
  });

  return (
    <Box>
      {suppliers && suppliers.length > 0 ? (
        <DataTable size="sm" mb={6} label="Suppliers table">
          <Table>
            <Thead>
              <Tr>
                <Th scope="col">Name</Th>
                <Th scope="col">Contact</Th>
                <Th scope="col">Address</Th>
                <Th scope="col">Designation</Th>
                <Th scope="col">Email</Th>
                <Th scope="col">Phone</Th>
                <Th scope="col">
                  <VisuallyHidden>Actions</VisuallyHidden>
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {suppliers.map((user, index) => (
                <Tr key={index}>
                  <Td fontWeight={600}>{user.name}</Td>
                  <Td>{user.contact}</Td>
                  <Td>{user.address}</Td>
                  <Td>{user.designation}</Td>
                  <Td>{user.email}</Td>
                  <Td>{user.phone}</Td>

                  <Td>
                    <HStack spacing={2} justify="flex-end">
                      <Button
                        variant="secondary"
                        size="xs"
                        leftIcon={<FiEdit2 />}
                        onClick={() => {
                          setSelectedUser({ ...user, index: index });
                          setEditEnabled(true);
                        }}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="danger-soft"
                        size="xs"
                        leftIcon={<FiTrash2 />}
                        onClick={() => {
                          updateData(suppliers.filter((_, i) => i !== index));
                        }}
                      >
                        Delete
                      </Button>
                    </HStack>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </DataTable>
      ) : (
        <EmptyState
          icon={FiTruck}
          title="No suppliers added"
          description='Fill in the form below and press "Add" to list a supplier reference.'
          py={6}
          headingAs="p"
        />
      )}
      <Grid templateColumns={{ base: "1fr", md: "repeat(4, 1fr)" }} gap={5}>
        <GridItem colSpan={{ base: 1, md: 2 }}>
          <FormControl>
            <FormLabel>Name</FormLabel>
            <Input
              type="text"
              isInvalid={!selectedUser?.name} // validation
              value={selectedUser?.name}
              onChange={(e) => {
                setSelectedUser({
                  ...selectedUser,
                  name: e.target.value,
                });
              }}
            />
          </FormControl>
        </GridItem>

        <GridItem colSpan={{ base: 1, md: 2 }}>
          <FormControl>
            <FormLabel>Contact</FormLabel>
            <Input
              type="text"
              isInvalid={!selectedUser?.contact} // validation
              value={selectedUser?.contact}
              onChange={(e) => {
                setSelectedUser({
                  ...selectedUser,
                  contact: e.target.value,
                });
              }}
            />
          </FormControl>
        </GridItem>

        {/* address */}
        <GridItem colSpan={{ base: 1, md: 2 }}>
          <FormControl>
            <FormLabel>Address</FormLabel>
            <Input
              type="text"
              isInvalid={!selectedUser?.address} // validation
              value={selectedUser?.address}
              onChange={(e) => {
                setSelectedUser({
                  ...selectedUser,
                  address: e.target.value,
                });
              }}
            />
          </FormControl>
        </GridItem>

        {/* designation */}
        <GridItem colSpan={{ base: 1, md: 2 }}>
          <FormControl>
            <FormLabel>Designation</FormLabel>
            <Input
              type="text"
              isInvalid={!selectedUser?.designation} // validation
              value={selectedUser?.designation}
              onChange={(e) => {
                setSelectedUser({
                  ...selectedUser,
                  designation: e.target.value,
                });
              }}
            />
          </FormControl>
        </GridItem>

        <GridItem colSpan={{ base: 1, md: 2 }}>
          <FormControl>
            <FormLabel>Email</FormLabel>
            <Input
              type="email"
              isInvalid={!selectedUser?.email} // validation
              value={selectedUser?.email}
              onChange={(e) => {
                setSelectedUser({
                  ...selectedUser,
                  email: e.target.value,
                });
              }}
            />
          </FormControl>
        </GridItem>
        <GridItem colSpan={1}>
          <FormControl>
            <FormLabel>Phone </FormLabel>
            <Input
              type="number"
              isInvalid={!selectedUser?.phone} // validation
              value={selectedUser?.phone}
              onChange={(e) => {
                setSelectedUser({
                  ...selectedUser,
                  phone: parseInt(e.target.value),
                });
              }}
            />
          </FormControl>
        </GridItem>

        <GridItem colSpan={1} display="flex" alignItems="flex-end">
          <Button
            w="full"
            variant="secondary"
            leftIcon={editEnabled ? <FiCheck /> : <FiPlus />}
            isDisabled={
              !selectedUser?.name ||
              !selectedUser?.contact ||
              !selectedUser?.address ||
              !selectedUser?.designation ||
              !selectedUser?.email ||
              !selectedUser?.phone
            }
            onClick={() => {
              if (editEnabled) {
                // update the data and don't forget to remove the index  but after updating the data
                const { index, ...newUser } = selectedUser;
                updateData(
                  suppliers.map((user, i) => (i === index ? newUser : user))
                );

                toast({
                  title: "Supplier Updated",
                  description: "Supplier has been updated successfully",
                  status: "warning",
                  duration: 9000,
                  isClosable: true,
                });

                setEditEnabled(false);
              } else {
                // remove the index before updating the data
                const { index, ...newUser } = selectedUser;
                updateData([...suppliers, newUser]);

                toast({
                  title: "Supplier Added",
                  description: "Supplier has been added successfully",
                  status: "success",
                  duration: 9000,
                  isClosable: true,
                });
              }
              setSelectedUser({
                name: "",
                contact: "",
                address: "",
                designation: "",
                email: "",
                phone: "",
                index: -1,
              });
            }}
          >
            {editEnabled ? "Save" : "Add"}
          </Button>
        </GridItem>
      </Grid>
    </Box>
  );
};

export default SuppliersStep;
